import { useCallback, useEffect, useMemo, useState } from "react";
import {
  App,
  Badge,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import type {
  CreateSubscriptionRequest,
  ExtendSubscriptionRequest,
  PortalSubscriptionRequest,
  Subscription,
  SubscriptionActionResponse,
  SubscriptionContext,
  SubscriptionContextResponse,
  SubscriptionsResponse,
} from "../../types/subscriptions";
import {
  canGrantFreePeriod,
  canOpenStripePortal,
  getSubscriptionState,
} from "../../types/subscriptions";
import { ActiveSubscriptionSwitch } from "../../components/ActiveSubscriptionSwitch";
import { useSupabaseUsers } from "../../lib/useSupabaseUsers";

const { Title, Paragraph, Text } = Typography;

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";
const STRIPE_CALLBACK_URL = `${window.location.origin}/subscriptions`;

function adminHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token");
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

async function adminFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: { ...adminHeaders(), ...init?.headers },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Request failed: ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const normalized = value.replace(/(\.\d{3})\d+/, "$1");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pl-PL");
}

function subscriptionRowKey(record: Subscription): string {
  return String(record.id ?? `${record.type_id ?? ""}-${record.status ?? ""}`);
}

function stateBadge(state: ReturnType<typeof getSubscriptionState>) {
  switch (state) {
    case "stripe":
      return <Badge status="error" text="Zarządzane przez Stripe" />;
    case "canceled":
      return <Badge status="default" text="Wygasła" />;
    default:
      return <Badge status="success" text="Aktywna" />;
  }
}

function noEmployeesTooltip(): string {
  return "Brak wolnych pracowników bez subskrypcji — nie można dodać nowej subskrypcji.";
}

export const SubscriptionsList = () => {
  const { notification } = App.useApp();
  const [userId, setUserId] = useState<string | undefined>();
  const [activeOnly, setActiveOnly] = useState(true);
  const { users, loading: usersLoading } = useSupabaseUsers(activeOnly, [userId]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [context, setContext] = useState<SubscriptionContext | null>(null);
  const [contextLoading, setContextLoading] = useState(false);

  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [stripeCheckoutModalOpen, setStripeCheckoutModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [extendDays, setExtendDays] = useState("");
  const [createDays, setCreateDays] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | undefined>();
  const [actionLoading, setActionLoading] = useState(false);

  const selectedUser = useMemo(
    () => users.find((user) => user.id === userId),
    [users, userId],
  );

  const canAddSubscription = context?.can_add_subscription ?? false;
  const canAddStripeSubscription =
    canAddSubscription && !!context?.stripe_customer_id && !!context?.stripe_subscription_type_id;

  const loadSubscriptions = useCallback(
    async (email: string) => {
      setSubscriptionsLoading(true);
      try {
        const params = new URLSearchParams({ email });
        const response = await adminFetch<SubscriptionsResponse>(
          `${API_URL}/api/v1/admin/subscriptions?${params.toString()}`,
        );
        setSubscriptions(response.data ?? []);
      } catch (error) {
        setSubscriptions([]);
        notification.error({
          message: "Nie udało się pobrać subskrypcji",
          description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
        });
      } finally {
        setSubscriptionsLoading(false);
      }
    },
    [notification],
  );

  const loadContext = useCallback(
    async (selectedUserId: string) => {
      setContextLoading(true);
      try {
        const params = new URLSearchParams({ user_id: selectedUserId });
        const response = await adminFetch<SubscriptionContextResponse>(
          `${API_URL}/api/v1/admin/subscriptions/context?${params.toString()}`,
        );
        setContext(response.data ?? null);
      } catch (error) {
        setContext(null);
        notification.error({
          message: "Nie udało się pobrać kontekstu subskrypcji",
          description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
        });
      } finally {
        setContextLoading(false);
      }
    },
    [notification],
  );

  useEffect(() => {
    if (selectedUser?.email && userId) {
      void loadSubscriptions(selectedUser.email);
      void loadContext(userId);
    } else {
      setSubscriptions([]);
      setContext(null);
    }
  }, [selectedUser?.email, userId, loadSubscriptions, loadContext]);

  const handleUserChange = (value: string | undefined) => {
    setUserId(value);
  };

  const reloadData = async () => {
    if (selectedUser?.email) {
      await loadSubscriptions(selectedUser.email);
    }
    if (userId) {
      await loadContext(userId);
    }
  };

  const openExtendModal = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setExtendDays("");
    setExtendModalOpen(true);
  };

  const handleExtend = async () => {
    if (!selectedSubscription) {
      notification.error({ message: "Brak wybranej subskrypcji" });
      return;
    }
    const days = Number.parseInt(extendDays, 10);
    if (!Number.isFinite(days) || days <= 0) {
      notification.error({ message: "Podaj poprawną liczbę dni" });
      return;
    }

    const isStripe = selectedSubscription.stripe_id != null;
    const payload: ExtendSubscriptionRequest = isStripe
      ? { days, stripe_subscription_id: selectedSubscription.stripe_id ?? undefined }
      : {
          days,
          sub_id: String(selectedSubscription.id),
          type_id: selectedSubscription.type_id,
        };

    if (!isStripe && (payload.sub_id == null || payload.type_id === undefined)) {
      notification.error({ message: "Brak wymaganych danych subskrypcji" });
      return;
    }

    setActionLoading(true);
    try {
      await adminFetch(`${API_URL}/api/v1/admin/subscriptions/extend`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      notification.success({
        message: isStripe
          ? "Darmowy okres został nadany w Stripe"
          : "Subskrypcja została przedłużona",
      });
      setExtendModalOpen(false);
      await reloadData();
    } catch (error) {
      notification.error({
        message: isStripe
          ? "Nie udało się nadać darmowego okresu"
          : "Nie udało się przedłużyć subskrypcji",
        description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreate = async () => {
    const participantId = context?.participant_id;
    if (!participantId) {
      notification.error({ message: "Brak participant_id dla wybranego klienta" });
      return;
    }
    const days = Number.parseInt(createDays, 10);
    if (!Number.isFinite(days) || days <= 0) {
      notification.error({ message: "Podaj poprawną liczbę dni" });
      return;
    }

    const payload: CreateSubscriptionRequest = {
      participant_id: participantId,
      days,
    };

    setActionLoading(true);
    try {
      await adminFetch(`${API_URL}/api/v1/admin/subscriptions`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      notification.success({ message: "Subskrypcja została dodana" });
      setCreateModalOpen(false);
      await reloadData();
    } catch (error) {
      notification.error({
        message: "Nie udało się dodać subskrypcji",
        description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStripeCheckout = async () => {
    if (!context?.participant_id) {
      notification.error({ message: "Brak participant_id dla wybranego klienta" });
      return;
    }
    if (!selectedEmployeeId) {
      notification.error({ message: "Wybierz pracownika" });
      return;
    }

    const payload: CreateSubscriptionRequest = {
      participant_id: context.participant_id,
      employee_id: selectedEmployeeId,
      callback: STRIPE_CALLBACK_URL,
    };

    setActionLoading(true);
    try {
      const response = await adminFetch<SubscriptionActionResponse>(
        `${API_URL}/api/v1/admin/subscriptions`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      if (response.url) {
        window.open(response.url, "_blank", "noopener,noreferrer");
        notification.success({ message: "Sesja Stripe Checkout została otwarta" });
        setStripeCheckoutModalOpen(false);
      } else {
        notification.error({ message: "Stripe nie zwrócił adresu URL sesji" });
      }
    } catch (error) {
      notification.error({
        message: "Nie udało się utworzyć sesji Stripe Checkout",
        description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStripePortal = async (_subscription: Subscription) => {
    if (!context?.stripe_customer_id) {
      notification.error({ message: "Brak stripe_customer_id dla uczestnika" });
      return;
    }

    const payload: PortalSubscriptionRequest = {
      customer_id: context.stripe_customer_id,
      callback: STRIPE_CALLBACK_URL,
    };

    setActionLoading(true);
    try {
      const response = await adminFetch<SubscriptionActionResponse>(
        `${API_URL}/api/v1/admin/subscriptions/portal`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      if (response.url) {
        window.open(response.url, "_blank", "noopener,noreferrer");
        notification.success({ message: "Portal Stripe został otwarty" });
      } else {
        notification.error({ message: "Stripe nie zwrócił adresu URL portalu" });
      }
    } catch (error) {
      notification.error({
        message: "Nie udało się otworzyć portalu Stripe",
        description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const visibleSubscriptions = subscriptions.filter((sub) => sub.id != null);
  const employees = context?.employees_without_subscription ?? [];

  const renderAddButtons = () => {
    const manualButton = (
      <Button
        type="default"
        disabled={!canAddSubscription}
        onClick={() => {
          setCreateDays("");
          setCreateModalOpen(true);
        }}
      >
        Dodaj nową subskrypcję
      </Button>
    );

    const stripeButton = (
      <Button
        type="primary"
        disabled={!canAddStripeSubscription}
        onClick={() => {
          setSelectedEmployeeId(undefined);
          setStripeCheckoutModalOpen(true);
        }}
      >
        Nowa subskrypcja Stripe
      </Button>
    );

    if (!canAddSubscription) {
      return (
        <Tooltip title={noEmployeesTooltip()}>
          <Space>
            {manualButton}
            {stripeButton}
          </Space>
        </Tooltip>
      );
    }

    if (!canAddStripeSubscription) {
      return (
        <Space>
          {manualButton}
          <Tooltip title="Brak stripe_customer_id lub stripe_subscription_type_id u uczestnika.">
            {stripeButton}
          </Tooltip>
        </Space>
      );
    }

    return (
      <Space>
        {manualButton}
        {stripeButton}
      </Space>
    );
  };

  return (
    <Card>
      <Title level={4}>Subskrypcje</Title>
      <Paragraph type="secondary">
        Wybierz użytkownika, aby wyświetlić i zarządzać jego subskrypcjami.
      </Paragraph>

      <Form layout="vertical" style={{ maxWidth: 480, marginBottom: 24 }}>
        <Form.Item
          label="Użytkownik"
          required
          extra={<ActiveSubscriptionSwitch checked={activeOnly} onChange={setActiveOnly} />}
        >
          <Select
            showSearch
            placeholder="Wybierz użytkownika"
            loading={usersLoading}
            value={userId}
            onChange={handleUserChange}
            optionFilterProp="label"
            options={users.map((user) => ({
              value: user.id,
              label: user.email || user.id,
            }))}
            allowClear
          />
        </Form.Item>
      </Form>

      {userId && (
        <>
          <Table<Subscription>
            rowKey={subscriptionRowKey}
            dataSource={visibleSubscriptions}
            loading={subscriptionsLoading || contextLoading}
            locale={{
              emptyText: "Ten klient nie ma aktywnych subskrypcji",
            }}
            pagination={false}
            scroll={{ x: true }}
          >
            <Table.Column<Subscription>
              title="Typ"
              render={(_, record) => record.type?.name ?? "—"}
            />
            <Table.Column<Subscription>
              dataIndex="status"
              title="Status"
              width={120}
              render={(value: string | undefined) => value ?? "—"}
            />
            <Table.Column<Subscription>
              title="Stan"
              width={220}
              render={(_, record) => stateBadge(getSubscriptionState(record))}
            />
            <Table.Column<Subscription>
              dataIndex="current_period_start_date"
              title="Zaczął się"
              width={180}
              render={(value: string | null | undefined) => formatDate(value)}
            />
            <Table.Column<Subscription>
              dataIndex="current_period_end_date"
              title="Kończy się"
              width={180}
              render={(value: string | null | undefined) => formatDate(value)}
            />
            <Table.Column<Subscription>
              dataIndex="last_action"
              title="Ostatnia akcja"
              width={180}
              render={(value: string | null | undefined) => formatDate(value)}
            />
            <Table.Column<Subscription>
              title="Akcje"
              width={280}
              render={(_, record) => {
                const state = getSubscriptionState(record);
                if (state === "active") {
                  return (
                    <Button type="primary" size="small" onClick={() => openExtendModal(record)}>
                      Przedłuż
                    </Button>
                  );
                }
                if (state === "stripe") {
                  return (
                    <Space wrap>
                      {canGrantFreePeriod(record) && (
                        <Button size="small" onClick={() => openExtendModal(record)}>
                          {record.status === "paused"
                            ? "Wznów z darmowym okresem"
                            : "Nadaj darmowy okres"}
                        </Button>
                      )}
                      {canOpenStripePortal(record, context) && (
                        <Button size="small" onClick={() => handleStripePortal(record)}>
                          Portal Stripe
                        </Button>
                      )}
                    </Space>
                  );
                }
                return <Text type="secondary">—</Text>;
              }}
            />
          </Table>

          <div style={{ marginTop: 16 }}>{renderAddButtons()}</div>
        </>
      )}

      <Modal
        title={
          selectedSubscription?.stripe_id != null
            ? selectedSubscription?.status === "paused"
              ? "Wznów z darmowym okresem"
              : "Nadaj darmowy okres"
            : "Przedłuż subskrypcję"
        }
        open={extendModalOpen}
        onCancel={() => setExtendModalOpen(false)}
        onOk={handleExtend}
        confirmLoading={actionLoading}
        okText={selectedSubscription?.stripe_id != null ? "Nadaj" : "Przedłuż"}
        cancelText="Anuluj"
      >
        <Form layout="vertical">
          <Form.Item label="O ile dni?" required>
            <Input
              inputMode="numeric"
              value={extendDays}
              onChange={(event) => setExtendDays(event.target.value.replace(/\D/g, ""))}
              placeholder="Ilość dni"
              onPressEnter={handleExtend}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Dodaj nową subskrypcję"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={handleCreate}
        confirmLoading={actionLoading}
        okText="Dodaj"
        cancelText="Anuluj"
      >
        <Form layout="vertical">
          <Form.Item label="O ile dni?" required>
            <Input
              inputMode="numeric"
              value={createDays}
              onChange={(event) => setCreateDays(event.target.value.replace(/\D/g, ""))}
              placeholder="Ilość dni"
              onPressEnter={handleCreate}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Nowa subskrypcja Stripe"
        open={stripeCheckoutModalOpen}
        onCancel={() => setStripeCheckoutModalOpen(false)}
        onOk={handleStripeCheckout}
        confirmLoading={actionLoading}
        okText="Utwórz checkout"
        cancelText="Anuluj"
      >
        <Form layout="vertical">
          <Form.Item label="Pracownik" required>
            <Select
              placeholder="Wybierz pracownika bez subskrypcji"
              value={selectedEmployeeId}
              onChange={setSelectedEmployeeId}
              options={employees.map((employeeId) => ({
                value: employeeId,
                label: employeeId,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};
